
import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Edit, Trash2, Clock, GripVertical, Play } from 'lucide-react';
import { getCategoryColor, getCategoryLabel } from './categoryUtils';
import { Activity } from '@/types/program';
import { isValidVideoUrl } from '@/utils/videoEmbedUtils';

interface ActivityItemProps {
  activity: Activity;
  index: number;
  isDragged: boolean;
  isDraggedOver: boolean;
  onEdit: (activity: Activity) => void;
  onDelete: (activityId: string) => void;
  onWatchVideo: (activity: Activity) => void;
  onDragStart: (e: React.DragEvent, index: number) => void;
  onDragOver: (e: React.DragEvent, index: number) => void;
  onDragLeave: () => void;
  onDrop: (e: React.DragEvent, index: number) => void;
}

export const ActivityItem = ({
  activity,
  index,
  isDragged,
  isDraggedOver,
  onEdit,
  onDelete,
  onWatchVideo,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop
}: ActivityItemProps) => {
  const hasValidVideo = activity.embed_url && isValidVideoUrl(activity.embed_url);
  return (
    <div 
      className={`flex items-center justify-between p-4 border rounded-lg transition-all duration-200 ${
        isDragged ? 'opacity-50 scale-95' : ''
      } ${
        isDraggedOver ? 'border-primary bg-primary/5' : ''
      }`}
      draggable
      onDragStart={(e) => onDragStart(e, index)}
      onDragOver={(e) => onDragOver(e, index)}
      onDragLeave={onDragLeave}
      onDrop={(e) => onDrop(e, index)}
    >
      <div className="flex items-center space-x-4">
        <div className="flex items-center space-x-2">
          <GripVertical className="h-5 w-5 text-muted-foreground cursor-grab active:cursor-grabbing" />
          <div className="flex items-center justify-center w-8 h-8 bg-primary text-primary-foreground rounded-full text-sm font-semibold">
            {index + 1}
          </div>
        </div>
        <div>
          <h4 className="font-medium">{activity.title}</h4>
          {activity.description && (
            <p className="text-sm text-muted-foreground">{activity.description}</p>
          )}
          <div className="flex items-center space-x-2 mt-1">
            <Badge className={getCategoryColor(activity.category)}>
              {getCategoryLabel(activity.category)}
            </Badge>
            <span className="text-sm text-muted-foreground flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {activity.duration} min
            </span>
          </div>
        </div>
      </div>
      <div className="flex space-x-2">
        {hasValidVideo && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onWatchVideo(activity)}
            className="text-blue-600 hover:text-blue-700"
          >
            <Play className="h-4 w-4" />
          </Button>
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={() => onEdit(activity)}
        >
          <Edit className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onDelete(activity.id)}
          className="text-red-600 hover:text-red-700"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};
